import { ActionParams } from './actions';
import { AppSettings, SaveFlowStatus } from '@src/types';
import { isAppEnabled } from '../selectors/ui-settings';
import { checkPendingChanges, setSaveStatus } from './save-status';
import { sendCookiesBannerUouSavePublishClick } from './bi';

export const enableAppAndSaveChanges = ({ getState, setState, services }: ActionParams) => async () => {
  const store = getState();
  const appEnabled = isAppEnabled(store);
  const isAppInstalled = store.isAppInstalled;

  sendCookiesBannerUouSavePublishClick(store);
  if (appEnabled && !isAppInstalled) {
    await services.provisionConsentPolicyApp();
  }
  await flushPendingChanges({ getState, setState, services })();
};

export const disableAppAndSaveChanges = (actionParams: ActionParams) => async () => {
  const { getState } = actionParams;

  await setPendingAppEnabled(actionParams)(false);
  sendCookiesBannerUouSavePublishClick(getState());
  await flushPendingChanges(actionParams)();
};

export const updateAppSettings = (actionParams: ActionParams) => async (pendingSettings: Partial<AppSettings>) => {
  const { getState, setState } = actionParams;
  const { pendingState } = getState();
  if (!pendingState) {
    return;
  }
  await setState({
    pendingState: {
      ...pendingState,
      appSettings: {
        ...pendingState.appSettings,
        ...pendingSettings,
      },
    },
  });
  await checkPendingChanges(actionParams)();
};

export const setPendingAppEnabled = (actionParams: ActionParams) => (enabled: boolean) =>
  updateAppSettings(actionParams)({ appEnabled: enabled });

export const updateCookiePolicyRequirement = (actionParams: ActionParams) => (requirementsApproved: boolean) => {
  updateAppSettings(actionParams)({ cookiePolicyRequirementApproved: requirementsApproved });
};

export const cancelPendingChanges = (actionParams: ActionParams) => async () => {
  const { getState, setState } = actionParams;
  const { currentState } = getState();
  await setState({ pendingState: currentState });
  checkPendingChanges(actionParams)();
};

export const flushPendingChanges = (actionParams: ActionParams) => async () => {
  const { getState, setState, services } = actionParams;
  const { currentState, pendingState } = getState();
  if (!currentState || !pendingState) {
    return;
  }
  const updatedState = { ...currentState, ...pendingState };

  setSaveStatus(actionParams)(SaveFlowStatus.Saving);

  try {
    await services.setComponentSettings({
      appSettings: updatedState.appSettings,
    });

    await services.ensurePolicyInSiteSettings(getState());
    setSaveStatus(actionParams)(isAppEnabled(getState()) ? SaveFlowStatus.SuccessfullySaved : SaveFlowStatus.None);
    await setState({ currentState: updatedState });
    checkPendingChanges(actionParams)();
  } catch {
    await setSaveStatus(actionParams)(SaveFlowStatus.FailedToSave);
  }
};

export const resetExpiryDate = (actionParams: ActionParams) => async () => {
  const { getState, setState, services } = actionParams;
  const { currentState, pendingState } = getState();
  if (!currentState || !pendingState) {
    return;
  }
  const newExpiryDate = Date.now();

  await services.setComponentSettings({
    appSettings: {
      expiryDate: newExpiryDate,
    },
  });

  await setState({
    currentState: {
      ...currentState,
      appSettings: {
        ...currentState.appSettings,
        expiryDate: newExpiryDate,
      },
    },
    pendingState: {
      ...pendingState,
      appSettings: {
        ...pendingState.appSettings,
        expiryDate: newExpiryDate,
      },
    },
  });
  await checkPendingChanges(actionParams)();
};
